import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Icon,
  Link,
  Modal,
  Pagination,
  Popover,
  SpaceBetween,
  Table,
  type TableProps,
} from '@amzn/awsui-components-react';
import { ActivityIcon } from '../../../components/ActivityIcon/ActivityIcon';

const COLUMN_IDS = [
  'account',
  'oppName',
  'matchReason',
  'suggestedAction',
  'status',
  'arr',
  'owner',
  'activities',
  'sfdcStage',
  'tags',
] as const;

const COLUMN_MIN_WIDTH: Record<(typeof COLUMN_IDS)[number], number> = {
  account: 160,
  oppName: 200,
  matchReason: 180,
  suggestedAction: 180,
  status: 140,
  arr: 110,
  owner: 110,
  activities: 200,
  sfdcStage: 140,
  tags: 160,
};

const WIDTHS_STORAGE_KEY = 'ivt_motion_pipeline_table_col_widths';

function loadColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, number>;
      return Object.fromEntries(
        Object.entries(parsed).filter(
          ([id, w]) => COLUMN_IDS.includes(id as (typeof COLUMN_IDS)[number]) && typeof w === 'number' && w > 0,
        ),
      );
    }
  } catch {
    /* ignore */
  }
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  getIvtClient,
  DeleteMotionPipelineCommand,
  type MotionOpp,
} from '../../../client/IvtHubClient';
import OverflowChips from '../../MotionsPage/components/OverflowChips';
import { oppSfdcUrl } from '../../../utils/sfdc';
import { denialHint } from '../../../utils/authorization';
import { activityCountLabel } from '../../MotionsPage/components/MotionActivityPopup';
import '../../MotionsPage/components/MotionActivityPopup.css';
import '../../MotionsPage/components/MotionPipelineList.css';

interface Props {
  motionId: string;
  opps: MotionOpp[];
  onReload?: () => void;
}

function formatArr(val: number | undefined | null): string {
  if (val == null) return '—';
  return `$${Math.round(val).toLocaleString('en-US')}`;
}

function StatusCell({ status }: { status?: string }) {
  if (status === 'TAGGED') {
    return (
      <span className="ivt-pipeline__status">
        <Icon name="status-positive" variant="success" />
        <span>Tagged</span>
      </span>
    );
  }
  if (status === 'SENT_FOR_TAGGING') {
    return (
      <span className="ivt-pipeline__status">
        <Icon name="status-in-progress" variant="link" />
        <span>Sent for Tagging</span>
      </span>
    );
  }
  return (
    <span className="ivt-pipeline__status">
      <Icon name="status-pending" variant="subtle" />
      <span>Pending</span>
    </span>
  );
}

function ActivityPopup({ opp, onClose }: { opp: MotionOpp; onClose: () => void }) {
  const activities = (opp as unknown as Record<string, unknown>).activities as Array<{
    activityId?: string;
    date?: string;
    activityType?: string;
    bdActivityType?: string;
    subject?: string;
    comments?: string;
    ownerAlias?: string;
    status?: string;
    isIvt?: boolean;
  }> | undefined;
  const count = opp.activityCount ?? 0;
  const title = opp.accountName ?? opp.oppName ?? opp.opptyId ?? '';

  return (
    <Modal visible onDismiss={onClose} header={`${title} (${activityCountLabel(count, opp.ivtActivityCount)})`}>
      {!activities || activities.length === 0 ? (
        <Box padding={{ vertical: 'l' }} textAlign="center" color="text-body-secondary">
          该 Opp 暂无活动记录。
        </Box>
      ) : (
        <ol className="ivt-activity-timeline">
          {activities.map((a) => (
            <li className="ivt-activity" key={a.activityId}>
              <span className="ivt-activity__dot" aria-hidden="true" />
              <div className="ivt-activity__body">
                <div className="ivt-activity__date">{a.date ?? '—'}</div>
                <div className="ivt-activity__head">
                  <ActivityIcon type={a.activityType} />
                  {a.isIvt && (
                    <span className="ivt-activity__ivt-tag" title="IVT 团队成员的活动">
                      [IVT]
                    </span>
                  )}
                  <span className="ivt-activity__subject" title={a.bdActivityType ?? undefined}>
                    {a.subject ?? '—'}
                  </span>
                  {a.ownerAlias && <span className="ivt-activity__owner">@{a.ownerAlias}</span>}
                  {a.status && (
                    <span
                      className={`ivt-activity__status${
                        a.status.toLowerCase() === 'completed' ? ' ivt-activity__status--done' : ''
                      }`}
                    >
                      {a.status}
                    </span>
                  )}
                </div>
                {a.comments && <div className="ivt-activity__comments">{a.comments}</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

const COLUMNS: TableProps.ColumnDefinition<MotionOpp>[] = [
  {
    id: 'account',
    header: 'Account',
    cell: (o) => o.accountName ?? '—',
    sortingField: 'accountName',
  },
  {
    id: 'oppName',
    header: 'Opp Name',
    cell: (o) => (
      <span className="ivt-pipeline__opp-name">
        <Link href={oppSfdcUrl(o.opptyId ?? '')} external target="_blank">
          {o.oppName ?? o.opptyId ?? '—'}
        </Link>
        <Popover
          dismissButton={false}
          position="top"
          size="small"
          content={<span>Opp ID: {o.opptyId}</span>}
        >
          <Icon name="status-info" variant="link" />
        </Popover>
      </span>
    ),
  },
  {
    id: 'matchReason',
    header: '命中原因',
    cell: (o) => o.matchReason ?? '—',
    sortingField: 'matchReason',
  },
  {
    id: 'suggestedAction',
    header: '建议行动',
    cell: (o) => o.suggestedAction ?? '—',
    sortingField: 'suggestedAction',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (o) => <StatusCell status={o.status} />,
    sortingField: 'status',
  },
  {
    id: 'arr',
    header: 'ARR',
    cell: (o) => formatArr(o.arr),
    sortingField: 'arr',
  },
  {
    id: 'owner',
    header: 'Owner',
    cell: (o) => (o.oppOwner ? `@${o.oppOwner}` : '—'),
    sortingField: 'oppOwner',
  },
  {
    id: 'activities',
    header: 'Activities',
    cell: () => null, // placeholder — overridden at render time
  },
  {
    id: 'sfdcStage',
    header: 'SFDC Stage',
    cell: (o) => o.oppStage ?? '—',
    sortingField: 'oppStage',
  },
  {
    id: 'tags',
    header: 'Tags',
    cell: (o) => <OverflowChips ariaLabel="Tags" items={(o.tags ?? []).map((t) => ({ key: t, label: t }))} />,
  },
];

export default function PipelineTable({ motionId, opps, onReload }: Props) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<MotionOpp[]>([]);
  const [activityRow, setActivityRow] = useState<MotionOpp | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);

  const { items, paginationProps, collectionProps } = useCollection(opps, {
    pagination: { pageSize },
    sorting: {},
    selection: {},
  });

  const columns: TableProps.ColumnDefinition<MotionOpp>[] = COLUMNS.map((col) => {
    const withResize = (c: TableProps.ColumnDefinition<MotionOpp>): TableProps.ColumnDefinition<MotionOpp> => {
      const id = c.id as (typeof COLUMN_IDS)[number];
      return {
        ...c,
        minWidth: COLUMN_MIN_WIDTH[id],
        ...(columnWidths[id] ? { width: columnWidths[id] } : {}),
      };
    };
    if (col.id === 'activities') {
      return withResize({
        ...col,
        cell: (o: MotionOpp) => {
          const count = o.activityCount ?? 0;
          return count > 0 ? (
            <button type="button" className="ivt-activity-trigger" onClick={() => setActivityRow(o)}>
              {activityCountLabel(count, o.ivtActivityCount)}
            </button>
          ) : (
            <span>{activityCountLabel(0, o.ivtActivityCount)}</span>
          );
        },
      });
    }
    return withResize(col);
  });

  const handleColumnWidthsChange = (widths: readonly number[]) => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      columns.forEach((col, i) => {
        if (col.id && typeof widths[i] === 'number') next[col.id] = widths[i];
      });
      saveColumnWidths(next);
      return next;
    });
  };

  const handleDelete = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await getIvtClient().send(new DeleteMotionPipelineCommand({
        motionId,
        opptyIds: selected.map((o) => o.opptyId!),
      }));
      setSelected([]);
      setDeleteModalVisible(false);
      onReload?.();
    } catch (e) {
      setActionError(denialHint(e) ?? ((e as Error).message || 'Delete failed'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
    <Table<MotionOpp>
      {...collectionProps}
      variant="container"
      columnDefinitions={columns}
      items={items}
      trackBy="id"
      selectionType="multi"
      selectedItems={selected}
      onSelectionChange={({ detail }) => setSelected([...detail.selectedItems])}
      sortingDisabled={false}
      resizableColumns
      onColumnWidthsChange={({ detail }) => handleColumnWidthsChange(detail.widths)}
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                disabled={selected.length === 0}
                // actionError is shared by both modals, so clear it on open —
                // otherwise a previous action's 403 greets the user before they
                // have confirmed anything here.
                onClick={() => { setActionError(null); setDeleteModalVisible(true); }}
              >
                Delete Pipeline
              </Button>
              <Button
                variant="normal"
                onClick={() => navigate(`/ivt/motion/wave-creation?motionId=${motionId}`)}
              >
                Create New Waves
              </Button>
            </SpaceBetween>
          }
        >
          Pipeline List
        </Header>
      }
      pagination={<Pagination {...paginationProps} />}
      preferences={
        <CollectionPreferences
          title="偏好设置"
          confirmLabel="确定"
          cancelLabel="取消"
          preferences={{ pageSize }}
          onConfirm={({ detail }) => setPageSize(detail.pageSize ?? 10)}
          pageSizePreference={{
            title: '每页条数',
            options: [
              { value: 10, label: '10 条 / 页' },
              { value: 20, label: '20 条 / 页' },
              { value: 50, label: '50 条 / 页' },
            ],
          }}
        />
      }
      empty={
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <SpaceBetween size="xs">
            <b>此 Motion 尚无 Pipeline</b>
            <span>点击 Create New Waves 开始圈选</span>
          </SpaceBetween>
        </Box>
      }
    />

    {activityRow && (
      <ActivityPopup opp={activityRow} onClose={() => setActivityRow(null)} />
    )}

    {/* Delete Pipeline confirmation modal */}
    <Modal
      visible={deleteModalVisible}
      onDismiss={() => { setActionError(null); setDeleteModalVisible(false); }}
      header="Delete Pipeline"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setDeleteModalVisible(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDelete} loading={actionLoading}>
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <span>确定要从此 Motion 中移除选中的 {selected.length} 条 Pipeline 吗？</span>
        {actionError && <Alert type="error">{actionError}</Alert>}
      </SpaceBetween>
    </Modal>
    </>
  );
}
